import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material';
import api from '../services/api';
import { normalizeInternationalPhone } from '../utils/membershipShipping';

const PRIMARY = '#00A7E3';

export default function MembershipShippingDialog({ open, onClose, user, onSaved }) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [country, setCountry] = useState('');
  const [zipcode, setZipcode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !user) return;
    setFirstName(user.firstName || '');
    setLastName(user.lastName || '');
    setPhone(user.phone || '');
    setAddressLine1(user.shippingLine1 || '');
    setAddressLine2(user.shippingLine2 || '');
    setCity(user.shippingCity || '');
    setState(user.shippingState || '');
    setCountry(user.shippingCountry || '');
    setZipcode(user.shippingZipcode || '');
    setError('');
  }, [open, user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const phoneNorm = normalizeInternationalPhone(phone);
    if (!phoneNorm) {
      setError('Enter a valid phone number with country code (e.g. +919876543210). Do not start with 0.');
      return;
    }
    if (!String(firstName).trim() || !String(lastName).trim()) {
      setError('First and last name are required.');
      return;
    }
    if (!String(addressLine1).trim() || !String(city).trim() || !String(state).trim() || !String(country).trim() || !String(zipcode).trim()) {
      setError('Please fill in address line 1, city, state, country, and zipcode.');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.put('/auth/membership-shipping', {
        firstName: String(firstName).trim(),
        lastName: String(lastName).trim(),
        phone: phoneNorm,
        addressLine1: String(addressLine1).trim(),
        addressLine2: String(addressLine2).trim(),
        city: String(city).trim(),
        state: String(state).trim(),
        country: String(country).trim(),
        zipcode: String(zipcode).trim(),
      });
      if (data.user && onSaved) await onSaved(data.user);
    } catch (err) {
      const d = err.response?.data;
      const msg =
        d?.message ||
        (Array.isArray(d?.errors) && d.errors[0]?.msg) ||
        'Could not save your details. Try again.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth scroll="paper">
      <form onSubmit={handleSubmit}>
        <DialogTitle sx={{ fontWeight: 800, pr: 6 }}>
          Shipping details required
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2, lineHeight: 1.65 }}>
            We need your full contact and shipping address to send your NFC card and membership materials. Complete this form to continue to checkout.
          </Typography>
          {error ? (
            <Typography variant="body2" color="error" sx={{ mb: 2 }}>
              {error}
            </Typography>
          ) : null}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0.5 }}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                label="First name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
                fullWidth
                size="small"
                sx={{ flex: '1 1 140px' }}
              />
              <TextField
                label="Last name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
                fullWidth
                size="small"
                sx={{ flex: '1 1 140px' }}
              />
            </Box>
            <TextField
              label="Phone number"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              required
              fullWidth
              size="small"
              placeholder="+91 98765 43210"
              helperText="Include country code"
            />
            <TextField
              label="Address line 1"
              value={addressLine1}
              onChange={(e) => setAddressLine1(e.target.value)}
              required
              fullWidth
              size="small"
            />
            <TextField
              label="Address line 2 (optional)"
              value={addressLine2}
              onChange={(e) => setAddressLine2(e.target.value)}
              fullWidth
              size="small"
            />
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField label="City" value={city} onChange={(e) => setCity(e.target.value)} required fullWidth size="small" sx={{ flex: '1 1 120px' }} />
              <TextField label="State / Province" value={state} onChange={(e) => setState(e.target.value)} required fullWidth size="small" sx={{ flex: '1 1 120px' }} />
            </Box>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField label="Country" value={country} onChange={(e) => setCountry(e.target.value)} required fullWidth size="small" sx={{ flex: '1 1 120px' }} />
              <TextField label="Zip / Postal code" value={zipcode} onChange={(e) => setZipcode(e.target.value)} required fullWidth size="small" sx={{ flex: '1 1 120px' }} />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button type="button" onClick={onClose} disabled={submitting} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={submitting}
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 1, bgcolor: PRIMARY, boxShadow: 'none', '&:hover': { bgcolor: '#0094cc', boxShadow: 'none' } }}
          >
            {submitting ? <CircularProgress size={22} color="inherit" /> : 'Save & continue to payment'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
